<template>
  <CustomTable
    :columns="columnsTableFields"
    :data="data"
    :is-loading="isLoading"
    :show-checkbox-row="false"
    :sort-direction="'asc'"
    :skeleton-row-number="1"
    :custom-class="'!tw-shadow-none road-map-table'"
    :header-wrapper-class="'tw-py-[20px] tw-px-[25px] tw-h-[56px]'"
  >
    <template #cell(order)="value">
      <span
        class="tw-text-[13px] tw-text-[#231F20] tw-font-medium tw-whitespace-nowrap"
        :class="
          value.item.checklistGroupCompletionPcnt === 1
            ? 'tw-text-[#B7B7B7]'
            : ''
        "
      >
        {{ value.item.index }}
      </span>
    </template>

    <template #cell(doWhat)="value">
      <TextAreaRoadmap
        :key="`roadmap-do-what-${value.item.checklistGroupId}`"
        :is-edit-status="isEditStatus"
        :item="value.item"
        :key-field="'checklistGroupName'"
        :custom-class="
          value.item.checklistGroupCompletionPcnt === 1
            ? 'tw-text-[#B7B7B7]'
            : ''
        "
        @edit-notes="emit('edit-notes')"
      />
    </template>

    <template #cell(how)="value">
      <TextAreaRoadmap
        :key="`roadmap-how-${value.item.checklistGroupId}`"
        :is-edit-status="isEditStatus"
        :item="value.item"
        :key-field="'checklistGroupActions'"
        :custom-class="
          value.item.checklistGroupCompletionPcnt === 1
            ? 'tw-text-[#B7B7B7]'
            : ''
        "
        @edit-notes="emit('edit-notes')"
      />
    </template>

    <template #cell(impact)="value">
      <div class="tw-flex tw-justify-center tw-gap-[10px]">
        <span class="">
          <QualityAssessment
            :data="value.item.checklistGroupBang"
            :is-completed="value.item.checklistGroupCompletionPcnt === 1"
            :type="QUALITY_ASSESSMENT_TYPE.IMPORTANCE"
          ></QualityAssessment>
        </span>
      </div>
    </template>

    <template #cell(effort)="value">
      <div class="tw-flex tw-justify-center tw-gap-[10px]">
        <span class="">
          <QualityAssessment
            :data="value.item.checklistGroupBuck"
            :is-completed="value.item.checklistGroupCompletionPcnt === 1"
            :type="QUALITY_ASSESSMENT_TYPE.COST"
          ></QualityAssessment>
        </span>
      </div>
    </template>

    <template #cell(status)="value">
      <div class="tw-flex tw-items-center tw-justify-center">
        <AssessmentChecklistStatusTag
          v-if="value.item.checklistGroupCompletionPcnt !== null"
          :checklist-status="
            value.item.checklistGroupCompletionPcnt === 1
              ? getComponentUiText(
                  'app-component-assessments-checklists-assessment-details-card-status-3',
                )
              : value.item.checklistGroupCompletionPcnt > 0 &&
                  value.item.checklistGroupCompletionPcnt < 1
                ? getComponentUiText(
                    'app-component-assessments-checklists-assessment-details-card-status-1',
                  )
                : getComponentUiText(
                    'app-component-assessments-checklists-assessment-details-card-status-2',
                  )
          "
          :checklist-status-id="
            value.item.checklistGroupCompletionPcnt === 1
              ? CHECKLISTSTATUS.DONE
              : value.item.checklistGroupCompletionPcnt > 0 &&
                  value.item.checklistGroupCompletionPcnt < 1
                ? CHECKLISTSTATUS.INPROG
                : CHECKLISTSTATUS.NEW
          "
          :is-loading="false"
        />
      </div>
    </template>
  </CustomTable>
</template>

<script lang="ts" setup>
import { getComponentUiText } from "@/utils/utilities";
import CustomTable, {
  CustomTableColumn,
} from "@/components/common/customTable.vue";
import { computed } from "vue";
import QualityAssessment from "./qualityAssessment.vue";
import AssessmentChecklistStatusTag from "@/components/assessment/assessmentChecklistStatusTag.vue";
import { CHECKLISTSTATUS, QUALITY_ASSESSMENT_TYPE } from "@/utils/constants";
import TextAreaRoadmap from "./textAreaRoadmap.vue";
export type roadmapTableColumn = {
  id: any;
  checklistGroupOrder: number;
  checklistGroupIsQuickWin: boolean;
  checklistGroupName: string;
  checklistGroupDescription: string;
  checklistGroupActions: string | null;
  checklistGroupBang: string;
  checklistGroupBuck: string;
};

const props = defineProps({
  data: {
    type: Array as () => roadmapTableColumn[],
    required: true,
    default: () => [],
  },
  isLoading: {
    type: Boolean,
    required: false,
  },
  columns: {
    type: Array,
    required: false,
    default: () => ["order", "doWhat", "how", "impact", "effort", "status"],
  },
  isEditStatus: {
    type: Boolean,
    required: false,
    default: false,
  },
});

const emit = defineEmits(["edit-notes"]);

const columnsTableFields = computed<CustomTableColumn[]>(() => {
  return [
    {
      text: getComponentUiText(
        "app-share-cyber-profile-roadmap-table-order-header-title-1",
      ),
      value: "order",
      headerClass:
        "!tw-text-[#ffffff] tw-text-[10px] tw-font-semibold tw-tracking-[1px] tw-leading-[100%] tw-uppercase tw-w-[10%]",
      valueClass:
        "tw-text-[#231F20] tw-text-[13px] tw-font-medium tw-w-[10%] tw-h-[70px]",
      sortable: false,
      sortDefault: false,
    },
    {
      text: getComponentUiText(
        "app-share-cyber-profile-roadmap-table-do-what-header-title-1",
      ),
      value: "doWhat",
      headerClass:
        "!tw-text-[#ffffff] tw-text-[10px] tw-font-semibold tw-tracking-[1px] tw-leading-[100%] tw-uppercase tw-w-[25%]",
      valueClass: `tw-text-[#231F20] tw-text-[13px] tw-font-normal tw-w-[25%] tw-items-center ${
        props.isEditStatus ? "!tw-pr-[10px] !tw-py-[12.5px]" : ""
      }`,
    },
    {
      text: getComponentUiText(
        "app-share-cyber-profile-roadmap-table-how-header-title-1",
      ),
      value: "how",
      headerClass:
        "!tw-text-[#ffffff] tw-text-[10px] tw-font-semibold tw-tracking-[1px] !tw-py-[unset] tw-leading-[100%] tw-uppercase  tw-w-[30%]",
      valueClass: `tw-text-[#231F20] tw-text-[13px] tw-font-normal  tw-w-[30%] tw-items-center ${
        props.isEditStatus ? "!tw-pr-[10px] !tw-py-[12.5px]" : ""
      }`,
    },
    {
      text: getComponentUiText(
        "app-share-cyber-profile-roadmap-table-importance-header-title-1",
      ),
      value: "impact",
      headerClass:
        "!tw-text-[#ffffff] tw-text-[10px] tw-font-semibold tw-tracking-[1px] tw-leading-[100%] tw-uppercase",
      valueClass: "tw-text-[#231F20] tw-text-[13px] tw-font-normal",
    },
    {
      text: getComponentUiText(
        "app-share-cyber-profile-roadmap-table-cost-header-title-1",
      ),
      value: "effort",
      headerClass:
        "!tw-text-[#ffffff] tw-text-[10px] tw-font-semibold tw-tracking-[1px] tw-leading-[100%] tw-uppercase ",
      valueClass: "tw-text-[#231F20] tw-text-[13px] tw-font-normal",
    },
    {
      text: getComponentUiText(
        "app-share-cyber-profile-roadmap-table-status-header-title-1",
      ),
      value: "status",
      headerClass:
        "!tw-text-[#ffffff] tw-text-[10px] tw-font-semibold tw-tracking-[1px] tw-leading-[100%] tw-uppercase",
      valueClass: "tw-text-[#231F20] tw-text-[13px] tw-font-normal",
    },
  ].filter((item) => props.columns.includes(item.value));
});
</script>
<style lang="scss" scoped>
:deep(.table-header) {
  height: 56px !important;
  background-color: #211f58 !important;
}
</style>
